import { DEFAULTS } from '../constants';
import { Segment } from '../types';
import { Lap } from '../types';
import { triggerVibration } from '../utils/hapticsUtils';

/**
 * Dependencies for the workout handlers
 */
export interface WorkoutHandlerDeps {
  /** Reset and start the timer */
  resetAndStart: () => void;
  /** Reset segment data for a new lap */
  resetLapSegmentData: (preserveLastSegmentPace?: boolean) => void;
  /** Reset lap data but preserve the last segment pace */
  resetLapDataPreservingPace: (completedLapPace?: number | null) => void;
  /** Start a new workout in the workout session hook */
  hookStartWorkout: () => void;
  /** Base record lap function from timer hook */
  baseRecordLap: (callback?: (lap: Lap) => void) => Lap | null;
  /** Add completed lap to the workout session */
  addCompletedLap: (lapData: Lap) => void;
  /** Record the final lap */
  recordFinalLap: (callback?: (lap: Lap) => void) => Lap | null;
  /** Pause the timer */
  pauseTimer: () => void;
  /** Complete workout in the session hook */
  hookCompleteWorkout: (finalLap: Lap | null, paceUnit?: 'mile' | 'km') => void;
  /** Set the current lap number */
  setCurrentLapNumber: React.Dispatch<React.SetStateAction<number>>;
  /** Base record segment function */
  baseRecordSegment: (distanceMeters: number) => {
    segment: Segment | null;
    lapPace: number | null;
  };
  /** Set the last segment pace directly */
  setLastSegmentPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Set the last completed lap pace directly */
  setLastCompletedLapPace: React.Dispatch<React.SetStateAction<number | null>>;
  /** Current lap pace (for calculation before reset) */
  currentLapPace: number | null;
  /** Whether timer is running */
  isRunning: boolean;
  /** Reset auto-lap toggle */
  resetAutoLap: () => void;
  /** Whether auto-lap is enabled */
  isAutoLapEnabled: boolean;
  /** Current pace unit */
  paceUnit: 'mile' | 'km';
}

/**
 * Workout handlers return type
 */
export interface WorkoutHandlers {
  /** Function to be called when workout timer lap button is pressed */
  handleWorkoutLap: () => void;
  /** Function to be called when workout timer start button is pressed */
  handleWorkoutStart: () => void;
  /** Wrapper for startTimer to add additional functionality */
  startTimer: () => void;
  /** Wrapper for recordLap to add callback handling */
  recordLap: () => Lap | null;
  /** Function to complete the workout */
  completeWorkout: () => void;
  /** Wrapper for recordSegment to simplify usage */
  recordSegment: (distanceMeters: number) => { segment: Segment | null; lapPace: number | null };
}

/**
 * Creates workout handlers for the stopwatch context
 */
export const createWorkoutHandlers = ({
  resetAndStart,
  resetLapSegmentData,
  resetLapDataPreservingPace,
  hookStartWorkout,
  baseRecordLap,
  addCompletedLap,
  recordFinalLap,
  pauseTimer,
  hookCompleteWorkout,
  setCurrentLapNumber,
  baseRecordSegment,
  setLastSegmentPace,
  setLastCompletedLapPace,
  currentLapPace,
  isRunning,
  resetAutoLap,
  isAutoLapEnabled,
  paceUnit,
}: WorkoutHandlerDeps): WorkoutHandlers => {
  /**
   * Function to be called when workout timer lap button is pressed
   */
  const handleWorkoutLap = (): void => {
    // Increment the current lap number locally
    setCurrentLapNumber(prevLapNumber => prevLapNumber + 1);

    // Reset auto-lap toggle to off
    resetAutoLap();
  };

  /**
   * Function to be called when workout timer start button is pressed
   */
  const handleWorkoutStart = (): void => {
    // Reset lap counter when workout starts
    setCurrentLapNumber(DEFAULTS.FIRST_LAP_NUMBER);

    // Start a new workout session in the hook
    hookStartWorkout();
  };

  /**
   * Wrapper for startTimer to add additional functionality
   */
  const startTimer = (): void => {
    triggerVibration();
    // Reset timer completely before starting a new workout
    resetAndStart();

    // Reset segment data for the new lap
    resetLapSegmentData();

    // Call onStart callback if it exists (to notify the parent component)
    handleWorkoutStart();

    // Reset auto-lap toggle to off when starting
    resetAutoLap();
  };

  /**
   * Wrapper for recordLap to add callback handling
   */
  const recordLap = (customCallback?: (lap: Lap) => void): Lap | null => {
    // Call the handleLap function to update lap number
    handleWorkoutLap();

    // If the timer is running, record a lap
    if (isRunning) {
      return baseRecordLap(lap => {
        // Call the addCompletedLap function to add the lap to the session
        addCompletedLap(lap);

        // Call custom callback if provided
        if (typeof customCallback === 'function') {
          customCallback(lap);
        }
      });
    }
    return null;
  };

  /**
   * Function to complete the workout
   */
  const completeWorkout = (): void => {
    triggerVibration();

    // If there's a valid currentLapPace, save it as the lastCompletedLapPace
    if (currentLapPace !== null && isFinite(currentLapPace)) {
      setLastCompletedLapPace(currentLapPace);
    }

    // Custom callback to ensure the final lap has the pace attached
    const finalLapCallback = (lap: Lap) => {
      // Explicitly attach the currentLapPace to the final lap
      if (currentLapPace !== null && isFinite(currentLapPace)) {
        lap.pace = currentLapPace;
      }
      // No need to call addCompletedLap here as hookCompleteWorkout will handle it
    };

    // Record final lap if needed (before stopping the timer), store the result
    const finalLap: Lap | null = recordFinalLap(finalLapCallback);

    // Pause the timer
    pauseTimer();

    // Call complete callback, passing the final lap data and current pace unit
    // Note: We're completing the workout but not resetting segment data
    // This ensures that pace calculations in lap display still work
    hookCompleteWorkout(finalLap, paceUnit);

    // Reset local lap counter
    // We don't call resetLapSegmentData here to preserve the segment data
    // for the lap display to show paces correctly
    setCurrentLapNumber(DEFAULTS.INITIAL_LAP_NUMBER);

    // Reset auto-lap toggle to off
    resetAutoLap();
  };

  /**
   * Wrapper for recordSegment to simplify usage
   */
  const recordSegment = (
    distanceMeters: number
  ): { segment: Segment | null; lapPace: number | null } => {
    // Record the segment using the base function, which now returns { segment, lapPace }
    const result = baseRecordSegment(distanceMeters);

    // If auto-lap is enabled, we want to preserve the last segment pace
    // The actual lap recording and auto-lap toggle reset happens in the DistanceButtons component
    // That component will call resetLapDataPreservingPace() before calling recordLap()
    if (!isAutoLapEnabled) {
      // Only reset auto-lap toggle for normal segment recording (not when auto-lap is enabled)
      resetAutoLap();
    }

    return result; // <-- Return the full result object
  };

  return {
    handleWorkoutLap,
    handleWorkoutStart,
    startTimer,
    recordLap,
    completeWorkout,
    recordSegment, // <-- Return the updated handler
  };
};
